import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Upload, Music, Users, Clock, Search, Trash2, GripVertical,
  CheckCircle, X, Download, LogOut, Crown, Star, MoreHorizontal,
  Mic2, Link2, Copy, Share2, Monitor, AlertTriangle,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useToast } from "@/hooks/use-toast";
import HostSettings from "@/components/HostSettings";
import ReportIssue from "@/components/ReportIssue";
import CustomSongApproval from "@/components/CustomSongApproval";
import DemoBanner from "@/components/DemoBanner";
import { supabase } from "@/integrations/supabase/client";
import makeOpenRoute from "@/lib/openRoute";
import { sendQueueUpdate, subscribeQueueUpdate } from "@/lib/queueSignal";
import { clearHostSession, requireAccountType } from "@/lib/hostAuth";
import ManageHostsSection from "@/components/ManageHostsSection";
import BillingDialog from "@/components/BillingDialog";
import RecoveryEmailModal from "@/components/RecoveryEmailModal";
import RecoveryEmailCard from "@/components/RecoveryEmailCard";
import { BILLING_ENABLED, type Currency, type Tier } from "@/lib/billing";
import Logo from "@/components/Logo";

interface Song {
  id: string;
  title: string;
  artist: string;
  genre: string;
  is_enabled: boolean;
  venue_id: string;
  created_at: string;
}

interface QueueRequest {
  id: string;
  song_id: string;
  singer_name: string;
  status: "queued" | "sung" | "cancelled" | "completed" | "removed";
  queue_position: number;
  created_at: string;
  custom_song_title?: string;
  custom_song_artist?: string;
  approval_status?: string;
  songs: { title: string; artist: string };
}

// ─── Skeleton shimmer block ────────────────────────────────────────────────────
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-white/10 rounded ${className}`} />
);

const HostDashboard = ({ demo = false }: { demo?: boolean } = { demo: false }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const venueRef = useRef<any>(null);

  // ── Server-verified auth guard (venue_admin only) ──────────────────────────
  useEffect(() => {
    if (demo) return;
    requireAccountType("venue_admin").then((result) => {
      if (!result.valid) {
        if (result.reason === "wrong_account_type") {
          // host accounts have no dashboard — send to show mode
          navigate("/host-show-mode");
        } else {
          clearHostSession();
          navigate("/venue");
        }
      }
    });
  }, [navigate, demo]);

  // ── Impersonation detection ────────────────────────────────────────────────
  const [impersonating] = useState(() => localStorage.getItem("impersonating") !== null);

  // ── Billing checkout dialog ─────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const [showBilling, setShowBilling] = useState(false);
  const [billingTier, setBillingTier] = useState<Tier>("regular");
  const [billingCurrency, setBillingCurrency] = useState<Currency>("NGN");

  // Recovery-email nudge — shown when this venue admin has no email on file.
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);

  // Auto-open checkout when arriving from a pricing page (?checkout=tier&currency=)
  useEffect(() => {
    if (demo) return;
    const checkout = searchParams.get("checkout");
    if (checkout === "regular" || checkout === "enterprise") {
      setBillingTier(checkout);
      const cur = searchParams.get("currency");
      if (cur === "NGN" || cur === "USD") setBillingCurrency(cur);
      setShowBilling(true);
      // Strip the params so a refresh doesn't reopen the dialog.
      searchParams.delete("checkout");
      searchParams.delete("currency");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, demo]);

  // ── Core state ────────────────────────────────────────────────────────────
  const [songs, setSongs] = useState<Song[]>([]);
  const [queue, setQueue] = useState<QueueRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [approvalFilter, setApprovalFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [venue, setVenue] = useState<any>(null);
  const [host, setHost] = useState<any>(null);

  useEffect(() => { venueRef.current = venue; }, [venue]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showViewMenu, setShowViewMenu] = useState(false);
  const viewMenuRef = useRef<HTMLDivElement | null>(null);
  const [showQueueMenu, setShowQueueMenu] = useState(false);
  const queueMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDemoBanner, setShowDemoBanner] = useState(true);

  // ── UI-only state ──────────────────────────────────────────────────────────
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);

  // ── Close menus on outside click ──────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (viewMenuRef.current && !viewMenuRef.current.contains(e.target as Node)) setShowViewMenu(false);
      if (queueMenuRef.current && !queueMenuRef.current.contains(e.target as Node)) setShowQueueMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Load host data ─────────────────────────────────────────────────────────
  const loadHostData = async () => {
    try {
      const token = localStorage.getItem("hostSessionToken");
      if (!token) return;
      // get_host_self returns the caller's own record (no password_hash) and
      // applies the expired→basic downgrade server-side.
      const { data, error } = await (supabase.rpc as any)("get_host_self", { p_token: token });
      const hostData = (Array.isArray(data) ? data[0] : data) as any;
      if (hostData && !error) {
        setHost(hostData);
        // No recovery email on file → prompt for one (reappears each login until set).
        if (!hostData.email) setShowRecoveryModal(true);
      }
    } catch (error) {
      console.error("Error loading host data:", error);
    }
  };

  // ── Demo banner timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!demo) return;
    const timer = setTimeout(() => setShowDemoBanner(false), 12000);
    return () => clearTimeout(timer);
  }, [demo]);

  // ── Load everything ────────────────────────────────────────────────────────
  useEffect(() => {
    loadHostData();
    if (demo) {
      const demoVenue = { id: "demo-venue-id", name: "Demo Venue", slug: "demo", welcome_message: "Welcome!", primary_color: "#7c3aed", secondary_color: "#06b6d4", allow_requests: true } as any;
      setVenue(demoVenue);
      localStorage.setItem("currentVenueId", demoVenue.id);
      setSongs([
        { id: "s1", title: "Demo Song 1", artist: "Artist A", genre: "Pop", is_enabled: true, venue_id: demoVenue.id, created_at: new Date().toISOString() },
        { id: "s2", title: "Demo Song 2", artist: "Artist B", genre: "Rock", is_enabled: true, venue_id: demoVenue.id, created_at: new Date().toISOString() },
      ]);
      setQueue([{ id: "q1", song_id: "s1", singer_name: "Demo Singer", status: "queued", queue_position: 1, created_at: new Date().toISOString(), songs: { title: "Demo Song 1", artist: "Artist A" } }]);
      setLoading(false);
      return;
    }
    loadVenueData();
    const subscription = supabase.channel("requests_changes").on("postgres_changes", { event: "*", schema: "public", table: "requests" }, () => { if (venueRef.current?.id) loadQueue(); }).subscribe();
    return () => { subscription.unsubscribe(); };
  }, [demo]);

  useEffect(() => {
    if (!venue?.id) return;
    const unsubscribe = subscribeQueueUpdate(() => loadQueue());
    return unsubscribe;
  }, [venue?.id]);

  useEffect(() => {
    if (venue?.id) loadQueue();
  }, [venue?.id]);

  // Songs are heavy — skip entirely for basic tier; wait for host to be known first
  useEffect(() => {
    if (!venue?.id || !host) return;
    if (host.subscription_level !== "basic") loadSongs();
  }, [venue?.id, host?.subscription_level]);

  const loadVenueData = async () => {
    try {
      const hostId = localStorage.getItem("hostId");
      if (!hostId) { setVenue({ name: "No host ID found" }); return; }
      const { data, error } = await (supabase.from("venue_hosts") as any).select(`venues (id, name, slug, welcome_message, primary_color, secondary_color, allow_requests)`).eq("host_id", hostId).single();
      if (error && error.code !== "PGRST116") throw error;
      if (data?.venues) { setVenue(data.venues); localStorage.setItem("currentVenueId", data.venues.id); }
      else setVenue({ name: "No venue assigned" });
    } catch (error) {
      console.error("Error loading venue:", error);
      setVenue({ name: "Error loading venue" });
    }
  };

  const loadSongs = async () => {
    try {
      if (!venue?.id) { setSongs([]); return; }
      const { data: initialSongs, error } = await supabase.from("songs").select("id, title, artist, genre, is_enabled, venue_id, created_at").eq("venue_id", venue.id).eq("is_enabled", true).order("title").order("id").limit(500);
      if (error) throw error;
      setSongs([...new Map((initialSongs || []).map((s) => [s.id, s])).values()] as any);
      const loadRemaining = async () => {
        try {
          let all = initialSongs || [];
          let offset = 500;
          let hasMore = (initialSongs?.length || 0) === 500;
          while (hasMore) {
            const { data: batch, error: batchErr } = await supabase.from("songs").select("id, title, artist, genre, is_enabled, venue_id, created_at").eq("venue_id", venue.id).eq("is_enabled", true).order("title").order("id").range(offset, offset + 999);
            if (batchErr) throw batchErr;
            if (batch && batch.length > 0) { all = [...all, ...batch]; offset += 1000; hasMore = batch.length === 1000; }
            else hasMore = false;
          }
          setSongs([...new Map(all.map((s) => [s.id, s])).values()] as any);
        } catch (e) { console.error("Error loading remaining songs:", e); }
      };
      setTimeout(loadRemaining, 100);
    } catch (error) {
      console.error("Error loading songs:", error);
      setSongs([]);
    }
  };

  const openRoute = makeOpenRoute({ demo, host, toast, onClose: () => setShowViewMenu(false) });

  const loadQueue = async () => {
    const v = venueRef.current;
    if (!v?.id) { setLoading(false); return; }
    try {
      const { data, error } = await (supabase.from("requests") as any).select(`id, song_id, singer_name, status, queue_position, created_at, custom_song_title, custom_song_artist, approval_status, songs ( title, artist )`).eq("venue_id", v.id).eq("status", "queued").eq("approval_status", "approved").order("queue_position");
      if (error) throw error;
      const deduped = data ? [...new Map((data as any[]).map((r) => [r.id, r])).values()] : [];
      setQueue(deduped as any);
    } catch (error) {
      console.error("Error loading queue:", error);
    } finally {
      setLoading(false);
    }
  };

  // ── Queue handlers ─────────────────────────────────────────────────────────
  const markAsSung = async (requestId: string) => {
    setActionLoading(requestId);
    try {
      const { error } = await supabase.from("requests").update({ status: "completed" }).eq("id", requestId);
      if (error) throw error;
      await loadQueue();
      try { sendQueueUpdate({ action: "completed", requestId }); } catch {}
      toast({ title: "Song Completed", description: "Request marked as sung" });
    } catch (error) {
      console.error("Error marking as sung:", error);
      toast({ title: "Error", description: "Failed to update request", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  // Animated wrapper — triggers exit animation before the DB call
  const handleMarkAsSung = async (requestId: string) => {
    setCompletingId(requestId);
    await new Promise((r) => setTimeout(r, 280));
    await markAsSung(requestId);
    setCompletingId(null);
  };

  const removeFromQueue = async (requestId: string) => {
    setActionLoading(requestId);
    try {
      const { error } = await supabase.from("requests").update({ status: "removed" }).eq("id", requestId);
      if (error) throw error;
      await loadQueue();
      toast({ title: "Removed", description: "Song removed from queue" });
    } catch (error) {
      console.error("Error removing:", error);
      toast({ title: "Error", description: "Failed to remove request", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const reorderQueue = async (draggedId: string, targetId: string) => {
    if (host?.subscription_level !== "enterprise") {
      toast({ title: "Enterprise Feature", description: "Queue reordering requires Enterprise", variant: "destructive" });
      return;
    }
    try {
      const draggedIndex = queue.findIndex((q) => q.id === draggedId);
      const targetIndex = queue.findIndex((q) => q.id === targetId);
      if (draggedIndex === -1 || targetIndex === -1) return;
      const newQueue = [...queue];
      const [item] = newQueue.splice(draggedIndex, 1);
      newQueue.splice(targetIndex, 0, item);
      await Promise.all(
        newQueue.map((q, index) =>
          supabase.from("requests").update({ queue_position: index + 1 }).eq("id", q.id)
        )
      );
      await loadQueue();
      toast({ title: "Queue Reordered" });
    } catch (error) {
      console.error("Error reordering:", error);
      toast({ title: "Error", description: "Failed to reorder", variant: "destructive" });
    }
  };

  const handleToggleRequests = async () => {
    if (!venue) return;
    try {
      const newStatus = !venue.allow_requests;
      // Via RPC: a direct venues update runs as anon (custom host session, not
      // Supabase Auth) and is silently denied by RLS, so the toggle never sticks.
      const { error } = await (supabase.rpc as any)("set_venue_allow_requests", {
        p_session_token: localStorage.getItem("hostSessionToken"),
        p_venue_id: venue.id,
        p_allow_requests: newStatus,
      });
      if (error) throw error;
      setVenue({ ...venue, allow_requests: newStatus });
      toast({ title: newStatus ? "Requests Enabled" : "Requests Disabled" });
    } catch (error) {
      console.error("Error toggling requests:", error);
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  const handleClearQueue = async () => {
    if (!confirm("Clear the entire queue? This cannot be undone.")) return;
    try {
      const { data: queued, error: fetchErr } = await supabase.from("requests").select("id").eq("venue_id", venue?.id).eq("status", "queued");
      if (fetchErr) throw fetchErr;
      if (!queued || queued.length === 0) { toast({ title: "Queue Empty" }); return; }
      const ids = queued.map((r) => r.id);
      const { error: clearErr } = await supabase.from("requests").update({ status: "removed" }).in("id", ids);
      if (clearErr) throw clearErr;
      await loadQueue();
      try { sendQueueUpdate({ action: "cleared" }); } catch {}
      toast({ title: "Queue Cleared", description: "All requests removed" });
    } catch (error) {
      console.error("Error clearing queue:", error);
      toast({ title: "Error", description: "Failed to clear queue", variant: "destructive" });
    }
  };

  const handleExportQueue = async () => {
    if (host?.subscription_level !== "enterprise") {
      toast({ title: "Enterprise Feature", description: "Export requires Enterprise", variant: "destructive" });
      return;
    }
    try {
      const rows = queue.map((req, i) => ({
        Position: req.queue_position || i + 1,
        Singer: req.singer_name,
        Song: req.songs?.title || req.custom_song_title || "Unknown",
        Artist: req.songs?.artist || req.custom_song_artist || "Unknown",
        Type: req.custom_song_title ? "Custom" : "Catalog",
        "Requested At": new Date(req.created_at).toLocaleString(),
      }));
      const headers = Object.keys(rows[0] || {});
      const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => { const v = String(r[h as keyof typeof r] ?? ""); return v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v; }).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", `${venue?.name || "Venue"}_Queue_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: "Queue Exported" });
    } catch (error) {
      console.error("Export error:", error);
      toast({ title: "Error", description: "Failed to export", variant: "destructive" });
    }
  };

  const handleDownloadPastRequests = async () => {
    if (host?.subscription_level !== "enterprise") {
      toast({ title: "Enterprise Feature", description: "Requires Enterprise", variant: "destructive" });
      return;
    }
    if (demo) { toast({ title: "Demo Mode", description: "Exports disabled in demo" }); return; }
    if (!venue?.id) { toast({ title: "No Venue", variant: "destructive" }); return; }
    setDownloadLoading(true);
    try {
      let query: any = supabase.from("requests").select("id, singer_name, custom_song_title, custom_song_artist, approval_status, status, queue_position, created_at").eq("venue_id", venue.id);
      if (approvalFilter !== "all") query = query.eq("approval_status", approvalFilter);
      if (startDate) query = query.gte("created_at", startDate);
      if (endDate) query = query.lte("created_at", `${endDate}T23:59:59`);
      const resp = await query.order("created_at", { ascending: false });
      if (resp.error) throw resp.error;
      const rows = resp.data || [];
      if (rows.length === 0) { toast({ title: "No results", description: "No requests found for selected filters" }); return; }
      const headers = ["Request ID", "Singer", "Song Title", "Song Artist", "Approval Status", "Status", "Queue Position", "Requested At"];
      const csv = [headers.join(","), ...rows.map((r: any) => [r.id, r.singer_name, r.custom_song_title || "", r.custom_song_artist || "", r.approval_status || "", r.status || "", r.queue_position ?? "", r.created_at || ""].map((v) => { const s = String(v); return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; }).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", `requests_${venue.id}_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: "Exported", description: `${rows.length} requests downloaded` });
    } catch (err) {
      console.error("Download error:", err);
      toast({ title: "Error", description: "Failed to download", variant: "destructive" });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (demo) { toast({ title: "Demo Mode", description: "CSV uploads disabled in demo" }); return; }
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== "text/csv") { toast({ title: "Invalid File", description: "Please upload a CSV file", variant: "destructive" }); return; }
    try {
      const text = await file.text();
      const lines = text.split("\n").filter((l) => l.trim());
      if (lines.length === 0) { toast({ title: "Empty File", variant: "destructive" }); return; }
      const header = lines[0].toLowerCase();
      if (!header.includes("client") || !header.includes("serial") || !header.includes("title") || !header.includes("artist")) {
        toast({ title: "Invalid CSV Format", description: "Need: Client ID, Serial Number, Song Title, Artist Name", variant: "destructive" });
        return;
      }
      const songs = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",").map((c) => c.trim().replace(/"/g, ""));
        if (cols.length >= 4 && venue?.id) {
          songs.push({ client_id: cols[0], serial_number: cols[1], title: cols[2], artist: cols[3], genre: "Unknown", venue_id: venue.id, is_enabled: true });
        }
      }
      if (songs.length === 0) { toast({ title: "No valid songs found", variant: "destructive" }); return; }
      toast({ title: "Processing CSV…", description: `${songs.length} songs found` });
      const { error } = await supabase.from("songs").insert(songs);
      if (error) throw error;
      await loadSongs();
      toast({ title: "Songs Added", description: `${songs.length} songs imported successfully` });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("CSV upload error:", error);
      toast({ title: "Upload Failed", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    clearHostSession();
    navigate("/host");
  };

  const handleExitImpersonation = () => {
    try {
      const saved = localStorage.getItem("_savedSuperAdmin");
      if (saved) {
        const parsed = JSON.parse(saved);
        localStorage.setItem("superAdminAuth", parsed.superAdminAuth);
        localStorage.setItem("userRole", parsed.userRole);
      }
    } catch {}
    localStorage.removeItem("hostAuth");
    localStorage.removeItem("hostId");
    localStorage.removeItem("impersonating");
    localStorage.removeItem("_savedSuperAdmin");
    navigate("/super-admin");
  };

  const handleCopyVenueLink = () => {
    const url = `${window.location.origin}/singer/${venue?.slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopyLinkSuccess(true);
      setTimeout(() => setCopyLinkSuccess(false), 1500);
    });
  };

  const handleDownloadQR = () => {
    const canvas = document.querySelector("[data-qr-canvas] canvas") as HTMLCanvasElement;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${venue?.name?.toLowerCase().replace(/\s+/g, "-") || "venue"}-qrcode.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  // ── Computed values ────────────────────────────────────────────────────────
  // Memoized: enterprise catalogs run into thousands of songs; without useMemo
  // every queue/realtime/state update rebuilt the array (4× toLowerCase per song).
  const filteredSongs = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (!q) return songs;
    return songs.filter((s) => s.title.toLowerCase().includes(q) || s.artist.toLowerCase().includes(q));
  }, [songs, searchTerm]);

  const SONG_LIST_RENDER_CAP = 200;
  const visibleSongs = filteredSongs.length > SONG_LIST_RENDER_CAP ? filteredSongs.slice(0, SONG_LIST_RENDER_CAP) : filteredSongs;

  const activeQueue = useMemo(
    () => [...new Map(queue.filter((r) => r.status === "queued").map((r) => [r.id, r])).values()],
    [queue]
  );

  const daysLeft = host?.subscription_end_date
    ? Math.ceil((new Date(host.subscription_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const progressPct = (() => {
    if (!host?.subscription_start_date || !host?.subscription_end_date) return 100;
    const start = new Date(host.subscription_start_date).getTime();
    const end = new Date(host.subscription_end_date).getTime();
    const remaining = Math.max(0, end - Date.now());
    const total = Math.max(1, end - start);
    return Math.min(100, (remaining / total) * 100);
  })();

  const tierLabel = host?.subscription_level === "enterprise" ? "Enterprise" : host?.subscription_level === "regular" ? "Regular" : "Basic";
  const TierIcon = host?.subscription_level === "enterprise" ? Crown : Star;
  const tierColour = host?.subscription_level === "enterprise" ? "text-console-signal" : host?.subscription_level === "regular" ? "text-console-primary" : "text-console-muted";

  const venueUrl = venue?.slug ? `${window.location.origin}/singer/${venue.slug}` : "";

  const ribbonState: "healthy" | "warning" | "expired" =
    daysLeft === null ? "healthy" : daysLeft <= 0 ? "expired" : daysLeft <= 30 ? "warning" : "healthy";

  // ── Stat cards config ──────────────────────────────────────────────────────
  const statCards = [
    ...(host?.subscription_level !== "basic" ? [{ label: "Total Songs", value: songs.length, icon: Music, colour: "text-console-primary", bg: "bg-console-primary/15" }] : []),
    { label: "In Queue", value: activeQueue.length, icon: Users, colour: "text-console-signal", bg: "bg-console-signal/15" },
    { label: "Songs Sung", value: queue.filter((q) => q.status === "completed").length, icon: CheckCircle, colour: "text-console-ok", bg: "bg-console-ok/15" },
    { label: "Est. Wait", value: `~${activeQueue.length * 4}m`, icon: Clock, colour: "text-console-muted", bg: "bg-white/5" },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist overflow-x-hidden relative">
      {/* Faint 48px grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
        }}
      />
      <div className="relative z-10">

      {/* ── Impersonation Banner ────────────────────────────────────────────── */}
      {impersonating && (
        <div className="w-full bg-console-signal/15 border-b border-console-signal/40 px-4 py-2.5 flex items-center justify-between gap-4">
          <p className="font-geist text-console-signal text-[13px] font-medium">
            Managing as <strong>{host?.display_name || "host"}</strong> — all actions are live on their account.
          </p>
          <Button size="sm" variant="outline" className="border-console-signal/50 text-console-signal hover:bg-console-signal/15 shrink-0 rounded-[6px]" onClick={handleExitImpersonation}>
            ← Exit to Admin
          </Button>
        </div>
      )}

      {/* ── Demo Banner ─────────────────────────────────────────────────────── */}
      {demo && showDemoBanner && <DemoBanner onDismiss={() => setShowDemoBanner(false)} message="DEMO MODE — actions are simulated." />}

      {/* ══ ZONE A — Header ═══════════════════════════════════════════════════ */}
      <header className="sticky top-0 z-40 bg-console-bg/85 backdrop-blur-sm border-b border-console-line">
        <div className="container mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-4">
          {/* Left: logo + venue + tier */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative">
              <Logo tone="text" size="sm" />
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-console-primary shadow-[0_0_6px_#4D7CFE]" />
            </div>
            <div className="flex flex-col leading-none min-w-0">
              <span className="font-geist font-semibold text-[14px] tracking-tight text-console-text truncate">{venue?.name || "Loading…"}</span>
              {host?.subscription_level && (
                <span className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted mt-1 flex items-center gap-1">
                  <span className={`inline-block w-1 h-1 rounded-full ${
                    host.subscription_level === "enterprise" ? "bg-console-signal" :
                    host.subscription_level === "regular" ? "bg-console-primary" :
                    "bg-console-muted"
                  }`} />
                  {tierLabel}
                </span>
              )}
            </div>
          </div>

          {/* Right: view switcher + settings + logout */}
          <div className="flex items-center gap-1 shrink-0">
            {/* View switcher */}
            <div ref={viewMenuRef} className="relative">
              <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-text hover:bg-white/5 rounded-[6px]" title="Switch view" onClick={() => setShowViewMenu((s) => !s)}>
                <Monitor className="h-4 w-4" />
              </Button>
              {showViewMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-console-surface border border-console-line rounded-[6px] shadow-xl z-50 py-1 overflow-hidden">
                  {[
                    { label: "Singer Page", action: () => openRoute("/demo-singer", () => venue?.slug ? `/singer/${venue.slug}` : null) },
                    { label: "Queue Display", action: () => openRoute(null, () => venue?.slug ? `/queue/${venue.slug}` : null) },
                  ].map(({ label, action }) => (
                    <button key={label} className="w-full min-h-[44px] flex items-center px-3 text-[13px] font-geist text-console-text hover:bg-white/5 text-left transition-colors" onClick={action}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <HostSettings host={host} />

            <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-red hover:bg-white/5 rounded-[6px]" title="Sign out" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <div className="container mx-auto px-4 sm:px-6 py-5 space-y-5">

        {/* ══ ZONE B — Subscription Ribbon ═════════════════════════════════════ */}
        {host?.subscription_level && (
          <div className={`rounded-[10px] border p-4 ${
            ribbonState === "expired" ? "bg-console-red/10 border-console-red/30"
            : ribbonState === "warning" ? "bg-console-signal/10 border-console-signal/30"
            : "bg-console-surface border-console-line"
          }`}>
            <div className="flex items-center justify-between gap-4 mb-2.5">
              <div className="flex items-center gap-2 min-w-0">
                {ribbonState === "expired" ? (
                  <AlertTriangle className="h-4 w-4 text-console-red shrink-0" />
                ) : (
                  <TierIcon className={`h-4 w-4 shrink-0 ${tierColour}`} />
                )}
                <span className="font-geist font-semibold text-[13px] text-console-text">{tierLabel} Plan</span>
                {daysLeft !== null && (
                  <span className={`font-geist-mono text-[11px] tracking-[0.06em] ml-1 ${ribbonState === "expired" ? "text-console-red" : ribbonState === "warning" ? "text-console-signal" : "text-console-muted"}`}>
                    {ribbonState === "expired" ? "Expired — contact info@nairoke.com" : `${daysLeft} days left`}
                  </span>
                )}
              </div>
              {ribbonState !== "healthy" ? (
                <Button
                  size="sm"
                  disabled={!BILLING_ENABLED}
                  title={!BILLING_ENABLED ? "Online billing is coming soon — contact info@nairoke.com" : undefined}
                  onClick={() => { setBillingTier(host?.subscription_level === "enterprise" ? "enterprise" : "regular"); setShowBilling(true); }}
                  className={`shrink-0 h-9 sm:h-8 px-3 text-[12px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px] disabled:opacity-50 disabled:cursor-not-allowed ${ribbonState === "expired" && BILLING_ENABLED ? "animate-pulse" : ""}`}
                >
                  {BILLING_ENABLED ? "Renew Now" : "Billing soon"}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!BILLING_ENABLED}
                  title={!BILLING_ENABLED ? "Online billing is coming soon — contact info@nairoke.com" : undefined}
                  onClick={() => { setBillingTier(host?.subscription_level === "enterprise" ? "enterprise" : "regular"); setShowBilling(true); }}
                  className="shrink-0 h-9 sm:h-8 px-3 text-[12px] font-medium font-geist text-console-muted hover:text-console-text hover:bg-white/5 border border-console-line rounded-[6px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Manage plan
                </Button>
              )}
            </div>
            {daysLeft !== null && ribbonState !== "expired" && (
              <div className="h-1.5 rounded-full bg-console-line-strong overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${ribbonState === "warning" ? "bg-console-signal" : "bg-console-ok"}`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* ══ Recovery email ════════════════════════════════════════════════════ */}
        {!demo && host && (
          <RecoveryEmailCard
            host={host}
            onSaved={(email) => setHost((prev: any) => (prev ? { ...prev, email } : prev))}
          />
        )}

        {/* ══ ZONE C — Stats ════════════════════════════════════════════════════ */}
        <div className={`grid gap-3 ${statCards.length === 4 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"}`}>
          {loading
            ? Array.from({ length: statCards.length }).map((_, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-12" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                </div>
              ))
            : statCards.map(({ label, value, icon: Icon, colour, bg }) => (
                <div key={label} className="rounded-[10px] border border-console-line bg-console-surface p-3 sm:p-4 hover:border-console-line-strong transition-colors">
                  <div className="flex flex-col xs:flex-row items-center xs:gap-3 gap-1 text-center xs:text-left">
                    <div className={`p-2 sm:p-2.5 ${bg} rounded-full shrink-0`}>
                      <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${colour}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-geist text-[18px] sm:text-[22px] font-semibold text-console-text leading-none tracking-[-0.02em]">{value}</p>
                      <p className="font-geist-mono text-[10px] tracking-[0.14em] uppercase text-console-muted mt-1">{label}</p>
                    </div>
                  </div>
                </div>
              ))
          }
        </div>

        {/* ══ ZONE D — Live Queue ═══════════════════════════════════════════════ */}
        <Card className="bg-console-surface border-console-line">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-signal">01 /</span>
                <CardTitle className="font-geist text-[15px] font-semibold tracking-tight text-console-text flex items-center gap-2">
                  <Users className="h-4 w-4 text-console-primary" />
                  Live Queue
                </CardTitle>
                {activeQueue.length > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-console-primary/20 text-console-primary font-geist-mono text-[11px] font-semibold animate-pulse">
                    {activeQueue.length}
                  </span>
                )}
              </div>
              {/* Queue action menu */}
              <div ref={queueMenuRef} className="relative">
                <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-text hover:bg-white/5 rounded-[6px]" onClick={() => setShowQueueMenu((s) => !s)}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
                {showQueueMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-console-surface border border-console-line rounded-[6px] shadow-xl z-50 py-1 overflow-hidden">
                    <button className="w-full min-h-[44px] flex items-center px-3 text-[13px] font-geist text-console-text hover:bg-white/5 transition-colors"
                      onClick={async () => { setShowQueueMenu(false); await handleToggleRequests(); }}>
                      {venue?.allow_requests !== false ? "Disable Requests" : "Enable Requests"}
                    </button>
                    <button className="w-full min-h-[44px] flex items-center px-3 text-[13px] font-geist text-console-red hover:bg-white/5 transition-colors"
                      onClick={async () => { setShowQueueMenu(false); await handleClearQueue(); }}>
                      Clear All Queue
                    </button>
                    <button className={`w-full min-h-[44px] flex items-center px-3 text-[13px] font-geist hover:bg-white/5 transition-colors ${host?.subscription_level !== "enterprise" ? "text-console-dim" : "text-console-text"}`}
                      onClick={async () => { setShowQueueMenu(false); await handleExportQueue(); }}>
                      Export Queue {host?.subscription_level !== "enterprise" && "(Enterprise)"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-white/10">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3.5 w-48" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <Skeleton className="h-8 w-20 rounded-md" />
                  </div>
                ))}
              </div>
            ) : activeQueue.length === 0 ? (
              /* Empty state */
              <div className="py-12 text-center space-y-3">
                <Mic2 className="h-10 w-10 mx-auto text-console-dim" />
                <div>
                  <p className="font-geist text-console-muted font-medium">Queue is empty</p>
                  <p className="font-geist text-console-dim text-[13px] mt-0.5">Share your venue page so guests can join</p>
                </div>
                {venueUrl && (
                  <button
                    onClick={handleCopyVenueLink}
                    className="inline-flex items-center gap-1.5 font-geist-mono text-[11px] text-console-primary hover:text-console-primary/80 transition-colors mt-1"
                  >
                    <Copy className="h-3 w-3" />
                    {copyLinkSuccess ? "Copied!" : venueUrl.replace("https://", "")}
                  </button>
                )}
              </div>
            ) : (
              activeQueue.map((request, index) => {
                const isNowPlaying = index === 0;
                const isCompleting = completingId === request.id;
                const isLoading = actionLoading === request.id;

                return (
                  <div
                    key={request.id}
                    draggable={host?.subscription_level === "enterprise"}
                    onDragStart={(e) => { if (host?.subscription_level === "enterprise") e.dataTransfer.setData("text/plain", request.id); }}
                    onDragOver={(e) => { if (host?.subscription_level === "enterprise") e.preventDefault(); }}
                    onDrop={(e) => { if (host?.subscription_level === "enterprise") { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id !== request.id) reorderQueue(id, request.id); } }}
                    className={`flex items-center gap-3 p-3 rounded-[6px] border transition-all duration-300 ${
                      isCompleting ? "translate-x-6 opacity-0" : "translate-x-0 opacity-100"
                    } ${
                      isNowPlaying
                        ? "border-console-signal/40 bg-console-signal/8 shadow-sm shadow-console-signal/10"
                        : "border-console-line bg-white/3 hover:bg-white/5"
                    } ${host?.subscription_level === "enterprise" ? "cursor-move" : ""}`}
                  >
                    {/* Drag handle */}
                    {host?.subscription_level === "enterprise" && (
                      <GripVertical className="h-4 w-4 text-white/20 shrink-0 hidden sm:block" />
                    )}

                    {/* Position / Now Playing indicator */}
                    {isNowPlaying ? (
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-console-signal/20 shrink-0">
                        <Mic2 className="h-4 w-4 text-console-signal" />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-white/5 border border-console-line shrink-0">
                        <span className="font-geist-mono text-[11px] font-semibold text-console-muted">{index + 1}</span>
                      </div>
                    )}

                    {/* Song / singer info */}
                    <div className="flex-1 min-w-0">
                      {isNowPlaying && (
                        <p className="font-geist-mono text-[10px] font-semibold text-console-signal uppercase tracking-[0.16em] mb-0.5 flex items-center gap-1.5">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-console-signal animate-pulse shadow-[0_0_6px_#F5B53D]" />
                          Now Playing
                        </p>
                      )}
                      <p className="font-geist font-semibold text-[14px] text-console-text truncate">
                        {request.songs?.title || request.custom_song_title}
                        {request.custom_song_title && (
                          <span className="ml-1.5 font-geist-mono text-[10px] tracking-[0.14em] bg-console-signal/20 text-console-signal px-1.5 py-0.5 rounded-[3px] font-medium">CUSTOM</span>
                        )}
                      </p>
                      <p className="font-geist text-[12px] text-console-muted truncate">{request.songs?.artist || request.custom_song_artist}</p>
                      <p className="font-geist text-[12px] text-console-primary truncate">{request.singer_name}</p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        className="h-11 sm:h-9 px-3 font-geist font-semibold text-[12px] bg-console-ok text-console-bg hover:bg-console-ok/90 rounded-[6px]"
                        disabled={isLoading || isCompleting}
                        onClick={() => handleMarkAsSung(request.id)}
                        title="Mark as sung"
                      >
                        <CheckCircle className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline ml-1">{isLoading ? "…" : "Done"}</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-11 w-11 sm:h-9 sm:w-9 px-0 text-console-red hover:bg-red-500/10 hover:text-console-red rounded-[6px]"
                        disabled={isLoading || isCompleting}
                        onClick={() => removeFromQueue(request.id)}
                        title="Remove from queue"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Custom Song Approvals — directly under Live Queue */}
        {venue?.id && <CustomSongApproval venueId={venue.id} showExportControls={false} />}

        {/* ══ ZONE E — Secondary Panels ═════════════════════════════════════════ */}
        <div className={`grid grid-cols-1 gap-5 items-start ${host?.subscription_level !== "basic" && venueUrl ? "lg:grid-cols-2" : ""}`}>

          {/* Song Database */}
          {host?.subscription_level !== "basic" && (
            <Card className="bg-console-surface border-console-line min-w-0 overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-signal">02 /</span>
                    <CardTitle className="font-geist text-[15px] font-semibold tracking-tight text-console-text flex items-center gap-2">
                      <Music className="h-4 w-4 text-console-primary" />
                      Song Database
                      <span className="font-geist-mono text-[11px] font-normal text-console-muted bg-white/5 px-2 py-0.5 rounded-[3px]">{songs.length}</span>
                    </CardTitle>
                  </div>
                </div>
                <CardDescription className="font-geist text-console-muted text-[12px] mt-1">Upload and manage your song collection</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Client ID */}
                <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-[6px] bg-console-primary/8 border border-console-primary/20">
                  <div className="flex-1 min-w-0">
                    <p className="font-geist-mono text-[10px] tracking-[0.14em] uppercase font-medium text-console-primary">Client ID</p>
                    <code className="font-geist-mono text-[11px] text-console-primary/80 truncate block">{venue?.id || "Loading…"}</code>
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-console-primary hover:text-console-primary hover:bg-white/5 shrink-0 rounded-[6px]"
                    onClick={() => { if (venue?.id) { navigator.clipboard.writeText(venue.id); toast({ title: "Copied", description: "Client ID copied" }); } }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* CSV upload zone */}
                <div className="border border-dashed border-white/15 rounded-lg p-5 text-center space-y-3 hover:border-white/25 transition-colors">
                  <Upload className="h-7 w-7 mx-auto text-white/30" />
                  <div>
                    <p className="text-sm font-medium text-white">Upload Song CSV</p>
                    <p className="text-xs text-white/40 mt-0.5">Client ID · Serial No · Song Title · Artist Name</p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <Button className="h-11 sm:h-9 text-[12px] font-geist font-semibold bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px] flex-1 sm:flex-none min-w-[120px]" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-3.5 w-3.5 mr-1.5" />
                      Choose File
                    </Button>
                    <Button variant="outline" className="h-8 text-xs border-white/15 text-white/70 hover:bg-white/5 flex-1 sm:flex-none min-w-[100px]"
                      onClick={() => { const a = document.createElement("a"); a.href = "/KaraokeCore Database Template.csv"; a.download = "KaraokeCore Database Template.csv"; document.body.appendChild(a); a.click(); document.body.removeChild(a); }}>
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Template
                    </Button>
                  </div>
                  <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                  <Input placeholder="Search songs…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary/50 pl-9 h-9 text-sm" />
                </div>

                {/* Song list */}
                <div className="space-y-1.5 max-h-56 overflow-y-auto overflow-x-hidden pr-1">
                  {filteredSongs.length === 0 ? (
                    <p className="text-center text-white/30 text-sm py-6">{searchTerm ? "No songs match your search" : "No songs yet — upload a CSV to get started"}</p>
                  ) : (
                    visibleSongs.map((song) => (
                      <div key={song.id} className="flex items-center justify-between p-2.5 rounded-lg border border-white/8 bg-white/4 hover:bg-white/6 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{song.title}</p>
                          <p className="text-xs text-white/40 truncate">{song.artist}</p>
                        </div>
                        <Button size="icon" variant="ghost" className="h-9 w-9 text-console-dim hover:text-console-red hover:bg-white/5 shrink-0 rounded-[6px]">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))
                  )}
                  {filteredSongs.length > SONG_LIST_RENDER_CAP && (
                    <p className="text-center text-white/40 text-xs py-3">
                      Showing first {SONG_LIST_RENDER_CAP} of {filteredSongs.length} — refine your search to narrow results
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Share Venue QR */}
          {venueUrl && (
            <Card className="bg-console-surface border-console-line min-w-0 overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2.5">
                  <span className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-signal">03 /</span>
                  <CardTitle className="font-geist text-[15px] font-semibold tracking-tight text-console-text flex items-center gap-2">
                    <Share2 className="h-4 w-4 text-console-ok" />
                    Share Venue
                  </CardTitle>
                </div>
                <CardDescription className="font-geist text-console-muted text-[12px] mt-1">Let singers scan or tap to join the queue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  {/* QR Code */}
                  <div data-qr-canvas className="shrink-0 mx-auto sm:mx-0 p-3 bg-white rounded-xl">
                    <QRCodeCanvas
                      value={venueUrl}
                      size={108}
                      bgColor="#ffffff"
                      fgColor="#1e1b4b"
                      level="H"
                    />
                  </div>

                  {/* URL + actions */}
                  <div className="w-full sm:flex-1 min-w-0 space-y-3">
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-white/5 border border-white/10">
                      <Link2 className="h-3.5 w-3.5 text-white/40 mt-0.5 shrink-0" />
                      <p className="text-xs text-white/70 break-all leading-relaxed min-w-0 flex-1">{venueUrl.replace(/^https?:\/\//, "")}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className={`flex-1 h-11 sm:h-9 text-[12px] font-geist font-semibold transition-colors rounded-[6px] ${copyLinkSuccess ? "bg-console-ok text-console-bg hover:bg-console-ok" : "bg-console-primary text-console-bg hover:bg-console-primary/90"}`}
                        onClick={handleCopyVenueLink}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        {copyLinkSuccess ? "Copied!" : "Copy Link"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-xs border-white/15 text-white/70 hover:bg-white/5"
                        onClick={handleDownloadQR}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download QR
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Past Requests Export */}
        {venue?.id && (
          <Card className="bg-console-surface border-console-line">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-signal">04 /</span>
                  <CardTitle className="font-geist text-[15px] font-semibold tracking-tight text-console-text flex items-center gap-2">
                    <Clock className="h-4 w-4 text-console-muted" />
                    Download Past Requests
                  </CardTitle>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={approvalFilter} onChange={(e) => setApprovalFilter(e.target.value as any)}
                    className="h-8 px-2 bg-white/5 border border-white/10 rounded-md text-xs text-white appearance-none">
                    <option value="all">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="h-8 px-2 bg-white/5 border border-white/10 rounded-md text-xs text-white" />
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className="h-8 px-2 bg-white/5 border border-white/10 rounded-md text-xs text-white" />
                  <Button size="sm" variant="outline"
                    className="h-8 text-xs border-white/15 text-white/70 hover:bg-white/5"
                    onClick={handleDownloadPastRequests}
                    disabled={downloadLoading || host?.subscription_level !== "enterprise"}>
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    {downloadLoading ? "Exporting…" : host?.subscription_level !== "enterprise" ? "Enterprise Only" : "Download"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-white/30">Exports include request details, approval status, and timestamps. Enterprise only.</p>
            </CardContent>
          </Card>
        )}

        {/* ── Manage Show Hosts (enterprise only) ──────────────────────── */}
        {host?.subscription_level === "enterprise" && venue?.id && (
          <ManageHostsSection venueId={venue.id} venueName={venue.name ?? "Your Venue"} />
        )}
        {host?.subscription_level !== "enterprise" && !demo && (
          <div className="rounded-[10px] border border-console-line bg-console-surface p-4 flex items-center gap-3 opacity-70">
            <Crown className="h-4 w-4 text-console-signal shrink-0" />
            <p className="font-geist text-[13px] text-console-muted">
              <span className="text-console-text font-medium">Show Hosts</span> — create dedicated accounts for staff who run your shows.{" "}
              <span className="text-console-signal">Enterprise plan required.</span>
            </p>
          </div>
        )}
      </div>

      <ReportIssue />
      </div>

      {!demo && (
        <BillingDialog
          open={showBilling}
          onOpenChange={setShowBilling}
          host={host}
          defaultTier={billingTier}
          defaultCurrency={billingCurrency}
        />
      )}

      {!demo && (
        <RecoveryEmailModal
          open={showRecoveryModal}
          onOpenChange={setShowRecoveryModal}
          onSaved={(email) => setHost((prev: any) => (prev ? { ...prev, email } : prev))}
        />
      )}
    </div>
  );
};

export default HostDashboard;
